import { createSelector } from 'reselect';

import { appSettings } from 'wanderu-config';
import {
    mapCarrierData,
    mapInfoSectionContent,
} from 'wanderu-ducks-seo';
import { processNewParagraph } from 'wanderu-utils';

import { mapHotels } from '../../utils';
import SLICE_NAMESPACE from '../types';
import NAMESPACE from './types';

const getStaticAssetsUrl = () => {
    return appSettings.config && appSettings.config.s3.staticAssetsUrl;
};

const selectData = (state) => {
    return state[SLICE_NAMESPACE][NAMESPACE];
};

const selectPageData = (state, airportId = '') => {
    return selectData(state)[airportId];
};

const getContent = (state, airportId = '') => {
    return selectPageData(state, airportId)?.content || {};
};

const getCarriers = (state, airportId = '') => {
    return selectPageData(state, airportId)?.carriers;
};

const getImages = (state, airportId = '') => {
    return selectPageData(state, airportId)?.images;
};

const getLocation = (state, airportId = '') => {
    return selectPageData(state, airportId)?.location || {};
};

const getAirportImages = (state, airportId = '') => {
    const wid = airportId.split('-')[2];

    return getImages(state, airportId)?.airport[wid] || [];
};

const getAirportMapImages = (state, airportId = '') => {
    const wid = airportId.split('-')[2];

    return getImages(state, airportId)?.airportMaps[wid] || {};
};

const getJourneys = (state, airportId = '') => {
    return selectPageData(state, airportId)?.journeys || [];
};

const getNearbyContent = (state, airportId = '') => {
    return selectPageData(state, airportId)?.nearby || [];
};

const getShuttles = (state, airportId = '') => {
    return selectPageData(state, airportId)?.shuttles || [];
};

const selectStats = (state, airportId = '') => {
    return selectPageData(state, airportId)?.stats || {};
};

const getRoutes = (state, airportId = '') => {
    return selectPageData(state, airportId)?.c2c || [];
};

const selectRoutes = createSelector(
    getRoutes,
    (routes) => {
        return routes
        .filter(route => !!route.c2c_url)
        .map((route) => ({
            avgPrice: route.avg_price_usd,
            avgTime: route.avg_time_min,
            dailyTrips: route.trips_per_day,
            destination: route.arrive_city_name,
            url: route.c2c_url,
        }));
    },
);

const selectLocation = createSelector(
    getLocation,
    (location) => ({
        ...location,
        formattedAddress: `${location.street1}, ${location.street2
            ? location.street2 + ', '
            : ''}${location.city}, ${location.stateCode}, ${location.countryCode}`,
    }),
);

const selectAirportImages = createSelector(
    getAirportImages,
    getAirportMapImages,
    (images = [], maps = {}) => {
        const allImages = [
            ...images,
            ...maps?.location || [],
        ];

        return allImages.map((image) => ({
            ...image,
            url: getStaticAssetsUrl() + image.url,
        }));
    },
);

const selectMetaImage = createSelector(
    selectAirportImages,
    (airportImages) => {
        const metaImage = airportImages[0] || {};

        return {
            height: metaImage.height,
            src: metaImage.url,
            width: metaImage.width,
        };
    },
);

const selectJourneys = createSelector(
    getJourneys,
    (journeys) => journeys,
);

const selectNearbyContent = createSelector(
    getNearbyContent,
    (nearby) => {
        return nearby
            .filter(content => !!content.title)
            .map((content) => ({
            stat: content.distance,
            text: content.title,
        }));
    },
);

const getCarrierContent = createSelector(
    getCarriers,
    (carrierData) => {
        if (!carrierData) {
            return undefined;
        }

        return carrierData.reduce((acc, carrier) => {
            Object.entries(carrier.content)
                .forEach(([key, value]) => {
                    if (key.includes(carrier.code)) {
                        acc[key] = value;
                    }

                    acc[`${carrier.code}_${key}`] = value;
                });
            return acc;
        }, {});
    },
);

const selectCarriers = createSelector(
    getCarriers,
    (carriers) => {
        const filteredCarriers = carriers?.filter(carrier => !!carrier.code)
        return mapCarrierData(filteredCarriers)
    },
);

const selectContent = createSelector(
    getContent,
    getCarrierContent,
    (content, carrierContent = {}) => ({
        ...content,
        ...carrierContent,
    }),
);

const selectFaqContent = createSelector(
    selectContent,
    mapInfoSectionContent,
);

const selectHotels = createSelector(
    selectContent,
    mapHotels,
);

const selectFaqStructuredContent = createSelector(
    selectFaqContent,
    (faqs) => {
        const faqStructuredDataList = faqs.reduce((arr, info) => {
            if (!info.title || !info.text) {
                return arr;
            }

            return [
                ...arr,
                {
                    '@type': 'Question',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: info.text,
                    },
                    name: info.title,
                },
            ];
        }, []);

        return {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqStructuredDataList,
        };
    },
);

const selectStructuredData = createSelector(
    selectFaqStructuredContent,
    (faqStructuredData) => {
        return [faqStructuredData];
    },
);

const selectShuttles = createSelector(
    getShuttles,
    shuttles => shuttles
        .filter(shuttle => !!shuttle.name)
        .map((shuttle) => ({
            ...processNewParagraph(shuttle),
            logo: getStaticAssetsUrl() + shuttle.logo,
        })),
);

const selectors = {
    selectAirportImages,
    selectCarriers,
    selectContent,
    selectFaqContent,
    selectHotels,
    selectJourneys,
    selectLocation,
    selectMetaImage,
    selectNearbyContent,
    selectRoutes,
    selectShuttles,
    selectStats,
    selectStructuredData,
};

export default selectors;
